import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabeledValue, SelectValue } from 'antd/lib/select';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import './index.less';

import { AgentRelationModel } from '../../../../../../gamma-api';
import IbModal from '../../../../../components/common/IbModal';
import IbButton from '../../../../../components/common/IbButton';
import IbDebounceSelect from '../../../../../components/common/IbDebounceSelect';
import { defaultApi } from '../../../../../apis';
import { alertsSelectorAdd, currentFolderIdSelector } from '../../../../../recoil';
import PromptEditorExpandable from '../../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../../components/common/PromptEditor/const';
import { CodeEditorSize } from '../../../../../components/common/CodeEditorExpandable/types';
import IbTypography from '../../../../../components/common/IbTypography';

import { FORM_ITEM_CLASS_NAME, MAIN_CLASS_NAME } from './const';

export interface IRelationModalProps {
  visible: boolean;
  relation?: AgentRelationModel;
  onSave?: (relation?: AgentRelationModel) => Promise<void>;
  onClose?: () => void;
}

const RelationModal: React.FC<IRelationModalProps> = ({ visible, relation, onSave, onClose }) => {
  const { t } = useTranslation();
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const folderId = useRecoilValue(currentFolderIdSelector);

  const [editRelation, setEditRelation] = useState<AgentRelationModel | undefined>(relation);
  const [selectedChildAgent, setSelectedChildAgent] = useState<SelectValue>();
  const [saving, setSaving] = useState(false);

  const fetchChildAgents = async (searchText: string): Promise<SelectValue[]> => {
    try {
      const response = await defaultApi.searchAgents(
        searchText,
        [folderId],
        undefined,
        relation ? [relation.managing_agent.agent_id] : undefined
      );
      return (response.data.items || []).map((agent) => ({ value: agent.agent_id, label: agent.name }));
    } catch (err) {
      addAlert({ content: t('Agent: Relation: Modal: Failed to load agents list'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      return [];
    }
  };

  const tryUpdateSelectedChildAgent = async () => {
    if (!editRelation?.managed_agent.agent_id) {
      setSelectedChildAgent(undefined);
      return;
    }

    const selectedChildAgentValue = selectedChildAgent ? (selectedChildAgent as LabeledValue).value : null;
    if (editRelation.managed_agent.agent_id === selectedChildAgentValue) {
      return;
    }

    try {
      const response = await defaultApi.getOneAgent(editRelation.managed_agent.agent_id);
      setSelectedChildAgent({ value: response.data.agent_id, label: response.data.name });
      setEditRelation({
        ...editRelation,
        managed_agent: response.data,
      } as AgentRelationModel);
    } catch (err) {
      addAlert({ content: t('Agent: Relation: Modal: Failed to load agent'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onChildAgentChange = (value: SelectValue) => {
    setSelectedChildAgent(value);
    setEditRelation({
      ...editRelation,
      managed_agent: {
        agent_id: (value as LabeledValue).value,
      },
    } as AgentRelationModel);
  };

  const onHandoffDescriptionChange = (value: string) => {
    setEditRelation({
      ...editRelation,
      handoff: { ...editRelation?.handoff, description: value },
    } as AgentRelationModel);
  };

  const onModalCancel = () => {
    onClose?.();
  };

  const onChildAgentIdChange = () => {
    tryUpdateSelectedChildAgent().finally();
  };
  useEffect(onChildAgentIdChange, [editRelation?.managed_agent.agent_id]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setEditRelation(relation);
  }, [visible]);

  const renderTitle = () =>
    editRelation?.relation_id ? t('Agent: Relation: Modal: Edit title') : t('Agent: Relation: Modal: Create title');

  const renderButtons = () => {
    const onSaveButtonClick = async () => {
      setSaving(true);
      await onSave?.(editRelation);
      setSaving(false);
    };

    return (
      <>
        <IbButton loading={saving} onClick={onSaveButtonClick}>
          {t('Agent: Relation: Modal: Save')}
        </IbButton>
        <IbButton type="secondary" onClick={onModalCancel}>
          {t('Agent: Relation: Modal: Cancel')}
        </IbButton>
      </>
    );
  };

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderButtons()}
      title={renderTitle()}
      visible={visible}
      onCancel={onModalCancel}
    >
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Relation: Modal: Managed agent: Title')}
        </IbTypography.Paragraph>
        <IbDebounceSelect
          showSearch
          fetchOptions={fetchChildAgents}
          value={selectedChildAgent}
          onChange={onChildAgentChange}
        />
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Relation: Modal: Handoff description: Title')}
        </IbTypography.Paragraph>
        <PromptEditorExpandable
          promptLanguage={PromptLanguage.PLAIN_TEXT}
          size={CodeEditorSize.XXL}
          value={editRelation?.handoff?.description || ''}
          onChange={onHandoffDescriptionChange}
        />
      </div>
    </IbModal>
  );
};

export default RelationModal;
